import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {SaveOutlined} from "@ant-design/icons";
import {Alert, App, Button, Col, DatePicker, Divider, Form, Input, Row, Segmented, Skeleton, Switch} from "antd";
import dayjs from "dayjs";
import {FileUploadUrlInput} from "../../../Components/FileUploadUrlInput";
import {Panel} from "../../../Components/ui";
import {AuthContext} from "../../../Contexts/AuthContext";
import {getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {
    DEFAULT_EVENT_PUBLIC_ASSET_MODE,
    EventPublicAssetMode,
    EventPublicAssetModeValue,
    normalizeEventPublicAssetMode
} from "../../../eventPublicAssetMode";
import {resolveLocalizedDatePickerFormat} from "../adminDateFormat";
import {HexColorInput} from "./HexColorInput";
import {
    buildEventBasicSetupSaveInput,
    colorRules,
    DEFAULT_PRIMARY_COLOR,
    DEFAULT_SECONDARY_COLOR,
    EventBasicSetupFormValues,
    eventBasicSetupToFormValues,
    requiredRule
} from "./eventConfigPayloads";
import {
    EventBasicSetupBranding
} from "./eventConfigQueries";
import {EventConfigScope, useEventBasicSetup} from "./useEventConfigScope";
import {
    useEventBasicSetupBrandingQuery,
    useSaveEventBasicSetupForConfigMutation
} from "../../../GraphQL/generated/graphql";

export const EventBasicsFormPanel: FC<{scope: EventConfigScope}> = ({scope: {eventNumericId, errorLabels}}) => {
    const {message} = App.useApp();
    const {t, i18n} = useTranslation();
    const {auth} = useContext(AuthContext);
    const [basicSetupForm] = Form.useForm<EventBasicSetupFormValues>();
    const [publicAssetMode, setPublicAssetMode] = useState<EventPublicAssetModeValue>(DEFAULT_EVENT_PUBLIC_ASSET_MODE);
    const localizedDatePickerFormat = useMemo(
        () => resolveLocalizedDatePickerFormat(i18n?.language),
        [i18n?.language]
    );
    const brandingColorLabels = useMemo(() => ({
        primaryColor: t("primary-color", {defaultValue: "Primary color"}),
        secondaryColor: t("secondary-color", {defaultValue: "Secondary color"}),
        requiredMessage: (label: string) => t("validation.color-required", {
            defaultValue: "{{field}} is required.",
            field: label
        }),
        invalidMessage: (label: string) => t("validation.hex-color", {
            defaultValue: "{{field}} must be a HEX color like #223F6B.",
            field: label
        })
    }), [t]);
    const fileUploadLabels = useMemo(() => ({
        upload: t("upload-file", {defaultValue: "Upload file"}),
        clear: t("clear", {defaultValue: "Clear"}),
        open: t("open", {defaultValue: "Open"}),
        empty: t("file-not-selected", {defaultValue: "No file selected"}),
        success: t("file-uploaded", {defaultValue: "File uploaded"}),
        error: t("error.upload-failed", {defaultValue: "Upload failed"})
    }), [t]);
    const publicAssetModeOptions = useMemo(() => [
        {
            label: t("asset-mode-combined-pdf", {defaultValue: "One PDF"}),
            value: EventPublicAssetMode.CombinedPdf
        },
        {
            label: t("asset-mode-separate-program-layout", {defaultValue: "Program + map"}),
            value: EventPublicAssetMode.SeparateProgramAndLayout
        }
    ], [t]);

    const {query: eventBasicSetupQuery, event: selectedBasicSetupEvent} = useEventBasicSetup(eventNumericId);
    const eventBasicSetupBrandingQuery = useEventBasicSetupBrandingQuery({
        variables: eventNumericId ? {eventId: eventNumericId} : undefined,
        skip: !eventNumericId,
        fetchPolicy: "cache-and-network"
    });
    const [saveEventBasicSetup, {loading: savingBasicSetup}] = useSaveEventBasicSetupForConfigMutation();

    const selectedBranding = useMemo(
        () => (eventBasicSetupBrandingQuery.data?.eventBrandings?.items ?? [])
            .find((item): item is EventBasicSetupBranding => !!item) ?? null,
        [eventBasicSetupBrandingQuery.data?.eventBrandings?.items]
    );
    const loadingBasicSetup = !!eventNumericId && (eventBasicSetupQuery.loading || eventBasicSetupBrandingQuery.loading);

    useEffect(() => {
        if (
            selectedBasicSetupEvent &&
            selectedBasicSetupEvent.id === eventNumericId &&
            !eventBasicSetupBrandingQuery.loading
        ) {
            const formValues = eventBasicSetupToFormValues(selectedBasicSetupEvent, selectedBranding);
            basicSetupForm.setFieldsValue(formValues);
            setPublicAssetMode(formValues.publicAssetMode);
        }
    }, [
        basicSetupForm,
        eventBasicSetupBrandingQuery.loading,
        eventNumericId,
        selectedBasicSetupEvent,
        selectedBranding
    ]);

    const handleSaveBasicSetup = async () => {
        if (!eventNumericId) {
            return;
        }

        const values = await basicSetupForm.validateFields();
        const startDate = values.startDate ? dayjs(values.startDate).startOf("day") : null;
        const endDate = values.endDate ? dayjs(values.endDate).startOf("day") : null;

        if (startDate?.isValid() && endDate?.isValid() && endDate.isBefore(startDate, "day")) {
            basicSetupForm.setFields([{
                name: "endDate",
                errors: [t("event-end-date-before-start", {
                    defaultValue: "End date must be on or after start date."
                })]
            }]);
            return;
        }

        basicSetupForm.setFields([{
            name: "endDate",
            errors: []
        }]);

        try {
            await saveEventBasicSetup({
                variables: {
                    input: buildEventBasicSetupSaveInput(eventNumericId, values, selectedBranding)
                }
            });

            message.success(t("event-basic-setup-saved", {defaultValue: "Event setup saved"}));
            await Promise.all([
                eventBasicSetupQuery.refetch(),
                eventBasicSetupBrandingQuery.refetch()
            ]);
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const handlePublicAssetModeChange = useCallback((value: string | number) => {
        const normalized = normalizeEventPublicAssetMode(value);

        setPublicAssetMode(normalized);
        basicSetupForm.setFieldsValue({publicAssetMode: normalized});
    }, [basicSetupForm]);

    const fileUploadProps = {
        authToken: auth.token,
        uploadText: fileUploadLabels.upload,
        clearText: fileUploadLabels.clear,
        openText: fileUploadLabels.open,
        emptyText: fileUploadLabels.empty,
        uploadSuccessText: fileUploadLabels.success,
        uploadErrorText: fileUploadLabels.error
    };

    return (
        <Panel
            title={t("event-basic-setup-guided", {defaultValue: "Event basics and public copy"})}
            extra={(
                <Button
                    type="primary"
                    icon={<SaveOutlined/>}
                    disabled={!eventNumericId}
                    loading={savingBasicSetup}
                    onClick={handleSaveBasicSetup}
                >
                    {t("save", {defaultValue: "Save"})}
                </Button>
            )}
        >
            {loadingBasicSetup && !selectedBasicSetupEvent ? <Skeleton active/> : (
                <>
                    {!eventNumericId && (
                        <Alert
                            type="info"
                            showIcon
                            message={t("event-required", {defaultValue: "Select an event"})}
                        />
                    )}
                    {(eventBasicSetupQuery.error || eventBasicSetupBrandingQuery.error) && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 16}}
                            message={t("error-loading-items", {defaultValue: "Error loading items"})}
                            description={getUserFacingErrorMessage(eventBasicSetupQuery.error ?? eventBasicSetupBrandingQuery.error, t, errorLabels)}
                        />
                    )}
                    <Form
                        form={basicSetupForm}
                        layout="vertical"
                        disabled={!eventNumericId || loadingBasicSetup || savingBasicSetup}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="name"
                                    label={t("name", {defaultValue: "Name"})}
                                    rules={[requiredRule(t("name-required", {defaultValue: "Name is required."}))]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="nameHr"
                                    label={t("name-hr", {defaultValue: "Name (Croatian)"})}
                                    rules={[requiredRule(t("name-hr-required", {defaultValue: "Croatian name is required."}))]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="nameEn"
                                    label={t("name-en", {defaultValue: "Name (English)"})}
                                    rules={[requiredRule(t("name-en-required", {defaultValue: "English name is required."}))]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="place"
                                    label={t("place", {defaultValue: "Place"})}
                                    rules={[requiredRule(t("place-required", {defaultValue: "Place is required."}))]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="address"
                                    label={t("address", {defaultValue: "Address"})}
                                    rules={[requiredRule(t("address-required", {defaultValue: "Address is required."}))]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="hasParticipationFee"
                                    label={t("has-participation-fee", {defaultValue: "Has participation fee"})}
                                    valuePropName="checked"
                                >
                                    <Switch
                                        checkedChildren={t("yes", {defaultValue: "Yes"})}
                                        unCheckedChildren={t("no", {defaultValue: "No"})}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="startDate"
                                    label={t("start-date", {defaultValue: "Start date"})}
                                    rules={[requiredRule(t("start-date-required", {defaultValue: "Start date is required."}))]}
                                >
                                    <DatePicker format={localizedDatePickerFormat} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="endDate"
                                    label={t("end-date", {defaultValue: "End date"})}
                                    rules={[requiredRule(t("end-date-required", {defaultValue: "End date is required."}))]}
                                >
                                    <DatePicker format={localizedDatePickerFormat} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Divider orientation="left">{t("public-registration-copy", {defaultValue: "Public registration copy"})}</Divider>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="eventInformationHr"
                                    label={t("event-information-hr", {defaultValue: "Event information (Croatian)"})}
                                    rules={[requiredRule(t("event-information-hr-required", {defaultValue: "Croatian event information is required."}))]}
                                >
                                    <Input.TextArea rows={4}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="eventInformationEn"
                                    label={t("event-information-en", {defaultValue: "Event information (English)"})}
                                    rules={[requiredRule(t("event-information-en-required", {defaultValue: "English event information is required."}))]}
                                >
                                    <Input.TextArea rows={4}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Divider orientation="left">{t("on-site-flow-copy", {defaultValue: "On-site and check-in instructions"})}</Divider>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="onSiteRegistrationInstructionsHr"
                                    label={t("on-site-registration-instructions-hr", {defaultValue: "On-site registration instructions (Croatian)"})}
                                    rules={[requiredRule(t("on-site-registration-instructions-hr-required", {defaultValue: "Croatian on-site instructions are required."}))]}
                                >
                                    <Input.TextArea rows={3}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="onSiteRegistrationInstructionsEn"
                                    label={t("on-site-registration-instructions-en", {defaultValue: "On-site registration instructions (English)"})}
                                    rules={[requiredRule(t("on-site-registration-instructions-en-required", {defaultValue: "English on-site instructions are required."}))]}
                                >
                                    <Input.TextArea rows={3}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Divider orientation="left">{t("branding-assets-certificate", {defaultValue: "Branding, documents, and certificate"})}</Divider>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="publicAssetMode"
                                    label={t("public-asset-mode", {defaultValue: "Public asset mode"})}
                                >
                                    <Segmented
                                        block
                                        options={publicAssetModeOptions}
                                        onChange={value => handlePublicAssetModeChange(value as string | number)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item name="logoUrl" label={t("logo-url", {defaultValue: "Event logo file"})}>
                                    <FileUploadUrlInput accept="image/*" {...fileUploadProps}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="programPdfUrl"
                                    label={publicAssetMode === EventPublicAssetMode.CombinedPdf
                                        ? t("combined-program-layout-pdf-url", {defaultValue: "Program and venue PDF file"})
                                        : t("program-pdf-url", {defaultValue: "Program PDF file"})}
                                >
                                    <FileUploadUrlInput accept=".pdf,application/pdf" {...fileUploadProps}/>
                                </Form.Item>
                            </Col>
                            {publicAssetMode === EventPublicAssetMode.SeparateProgramAndLayout ? (
                                <Col xs={24} md={8}>
                                    <Form.Item name="venueLayoutImageUrl" label={t("venue-layout-image-url", {defaultValue: "Venue map image file"})}>
                                        <FileUploadUrlInput accept="image/*" {...fileUploadProps}/>
                                    </Form.Item>
                                </Col>
                            ) : null}
                            <Col xs={24} md={8}>
                                <Form.Item name="sponsorsImageUrl" label={t("sponsors-image-url", {defaultValue: "Sponsor image file"})}>
                                    <FileUploadUrlInput accept="image/*" {...fileUploadProps}/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="primaryColor"
                                    label={brandingColorLabels.primaryColor}
                                    rules={colorRules(brandingColorLabels.primaryColor, brandingColorLabels)}
                                >
                                    <HexColorInput
                                        placeholder={DEFAULT_PRIMARY_COLOR}
                                        openPickerLabel={t("open-color-picker", {defaultValue: "Open color picker"})}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="secondaryColor"
                                    label={brandingColorLabels.secondaryColor}
                                    rules={colorRules(brandingColorLabels.secondaryColor, brandingColorLabels)}
                                >
                                    <HexColorInput
                                        placeholder={DEFAULT_SECONDARY_COLOR}
                                        openPickerLabel={t("open-color-picker", {defaultValue: "Open color picker"})}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </>
            )}
        </Panel>
    );
};
